import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import DiamondMark from "@/components/DiamondMark";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [mfaStep, setMfaStep] = useState(false);
  const [code, setCode] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const data = await login(email, password, mfaStep ? code : undefined);
      if (data.mfa_required) {
        setMfaStep(true);
        setBusy(false);
        return;
      }
      if (data.pending) {
        navigate("/admin");
        return;
      }
      toast.success("Welcome back.");
      navigate("/admin");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail);
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const inputCls = "w-full bg-transparent border-b border-white/20 focus:border-bd-gold outline-none py-3 text-bd-ivory placeholder:text-white/30 transition-colors";

  return (
    <div data-testid="page-login" className="min-h-screen bg-bd-black grain flex items-center justify-center px-6 relative">
      <div className="w-full max-w-md relative z-10">
        <Link to="/" className="flex items-center justify-center gap-3 mb-10">
          <DiamondMark size={36} />
          <span className="text-bd-ivory font-serif text-2xl">Black Diamond</span>
        </Link>
        <div className="bg-bd-charcoal border border-white/10 p-8 md:p-10">
          <p className="overline text-bd-gold mb-3 text-center">Group Portal</p>
          <h1 className="font-serif text-3xl text-bd-ivory text-center mb-8">Secure sign in</h1>

          <form onSubmit={submit} className="space-y-6">
            {!mfaStep ? (
              <>
                <input data-testid="login-email" type="email" className={inputCls} placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} />
                <input data-testid="login-password" type="password" className={inputCls} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
              </>
            ) : (
              <div>
                <p className="text-sm text-bd-ivory/60 mb-4">Enter the 6-digit code from your authenticator app.</p>
                <input data-testid="login-mfa-code" inputMode="numeric" autoFocus maxLength={6} className={`${inputCls} tracking-[0.5em] text-center text-lg`} placeholder="000000" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
              </div>
            )}
            {error && <p data-testid="login-error" role="alert" className="text-sm text-red-400">{error}</p>}
            <button data-testid="login-submit" type="submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-bd-gold text-bd-black hover:bg-bd-gold2 transition-colors duration-300 px-8 py-4 text-sm tracking-[0.15em] uppercase disabled:opacity-60">
              {busy && <Loader2 size={16} className="animate-spin" />} {mfaStep ? "Verify & Sign In" : "Sign In"}
            </button>
          </form>

          {!mfaStep && (
          <>
          <div className="flex items-center gap-4 my-7">
            <span className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-bd-ivory/30 tracking-widest uppercase">or</span>
            <span className="flex-1 h-px bg-white/10" />
          </div>

          <button data-testid="login-google" onClick={googleLogin} className="w-full flex items-center justify-center gap-3 border border-white/20 text-bd-ivory hover:border-bd-gold hover:text-bd-gold transition-colors duration-300 px-8 py-4 text-sm tracking-wide">
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#B89C65" d="M21.35 11.1h-9.17v2.9h5.27c-.23 1.4-1.64 4.1-5.27 4.1-3.17 0-5.76-2.62-5.76-5.85s2.59-5.85 5.76-5.85c1.8 0 3.01.77 3.7 1.43l2.52-2.43C16.97 3.6 14.83 2.7 12.18 2.7 7.08 2.7 2.95 6.82 2.95 12s4.13 9.3 9.23 9.3c5.33 0 8.86-3.75 8.86-9.03 0-.6-.07-1.06-.16-1.17z"/></svg>
            Continue with Google
          </button>
          </>
          )}

          <p className="mt-8 text-xs text-bd-ivory/30 text-center leading-relaxed">
            This area is restricted to authorised personnel of Black Diamond Ventures Group. MFA-ready secure access.
          </p>
        </div>
        <Link to="/" className="block text-center mt-8 text-sm text-bd-ivory/40 hover:text-bd-gold transition-colors">← Return to website</Link>
      </div>
    </div>
  );
}
